import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Laptop, Smartphone } from "lucide-react";

import { AccountShell } from "../../components/desktop/account-nav.tsx";
import { Button, Empty, ErrorBlock, Spinner } from "../../components/ui.tsx";
import { cn } from "@/lib/utils";
import { api } from "../../api/client.ts";
import { useMe } from "../../api/queries.ts";
import { ChangePassword } from "../../components/change-password.tsx";

/**
 * ═══════════════════════════════════════════════════════════════
 *  网页版 · 账号安全（登录设备）
 * ═══════════════════════════════════════════════════════════════
 *
 * 🔴 这个平台上的账号是有钱的（分销商账上有余额、能提现），
 *    "我的账号在哪些设备登过、能不能踢掉"是刚需而不是锦上添花。
 *
 * ⚠️ 网页上把「登录时间 / 到期时间」都摆出来 —— 手机上放不下所以只出登录时间，
 *    但判断"这个登录是不是我"的时候，两个时间一起看才说明问题。
 */

interface SessionRow {
  id: number;
  userAgent: string;
  createdAt: string;
  expiresAt: string;
  /** 服务端标出来的，前端拿不到 jti。 */
  isCurrent: boolean;
}

/**
 * 从 UA 里认出设备。
 * ⚠️ 只做粗略识别 —— UA 本来就不可靠，认不出就说"其他设备"，别硬猜出个具体型号。
 */
function deviceOf(ua: string): { name: string; mobile: boolean } {
  if (!ua) return { name: "未知设备", mobile: false };
  if (/iPhone|iPad/i.test(ua)) return { name: "iPhone / iPad", mobile: true };
  if (/Android/i.test(ua)) return { name: "Android 手机", mobile: true };
  if (/Macintosh/i.test(ua)) return { name: "Mac", mobile: false };
  if (/Windows/i.test(ua)) return { name: "Windows 电脑", mobile: false };
  if (/MicroMessenger/i.test(ua)) return { name: "微信内置浏览器", mobile: true };
  return { name: "其他设备", mobile: false };
}

export function DesktopSecurity() {
  const qc = useQueryClient();
  const { data: me, isPending: mePending } = useMe();

  const sessions = useQuery({
    queryKey: ["me", "sessions"],
    queryFn: () => api.get<SessionRow[]>("/auth/me/sessions"),
    enabled: Boolean(me),
  });

  const revokeOthers = useMutation({
    mutationFn: () => api.post<{ revoked: number }>("/auth/me/sessions/revoke-others", {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["me", "sessions"] }),
  });

  if (mePending)
    return (
      <AccountShell title="账号安全">
        <Spinner compact />
      </AccountShell>
    );
  if (!me)
    return (
      <AccountShell title="账号安全">
        <Empty title="请先登录" action={{ label: "去登录", to: "/login" }} compact />
      </AccountShell>
    );

  const others = (sessions.data ?? []).filter((s) => !s.isCurrent).length;

  return (
    <AccountShell title="账号安全">
      {/*
        🔴 **修改密码以前哪儿都没有**（接口早就有，前端一处没引用）。

        ⚠️ **卡片跟下面的设备列表一样满宽**（老板：修改密码的容器宽度
           不到登录设备的一半）。同一页里两块内容一宽一窄，看着像没做完。

        ⚠️ 但**输入框本身不跟着拉到 1440**（那比窄的更难用）——
           三个密码框在里面排成一行（`columns` 让组件自己横排），
           宽度被内容用掉了，每个框还在正常尺寸上。
      */}
      <section className="mb-6 rounded-xl border border-border bg-card p-5">
        <h2 className="mb-4 text-[15px] font-medium">修改密码</h2>
        <ChangePassword columns />
      </section>

      <div className="mb-5 rounded-xl border border-brand-200 bg-brand-50 px-5 py-4 text-[14px] leading-relaxed text-brand-700">
        这里是你的账号登录过的设备。这个账号上有余额、能提现 ——
        看到不认识的设备，用下面的按钮把它们全踢掉。
      </div>

      {sessions.isPending ? (
        <Spinner compact />
      ) : sessions.error ? (
        <ErrorBlock error={sessions.error} onRetry={() => sessions.refetch()} />
      ) : (
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          {/* ⚠️ 表头在网页上才有意义 —— 手机上三列挤不下。 */}
          <div className="grid grid-cols-[minmax(0,1fr)_200px_200px] gap-4 border-b border-border bg-secondary/40 px-5 py-2.5 text-[13px] text-muted-foreground">
            <span>设备</span>
            <span>登录时间</span>
            <span>登录有效期至</span>
          </div>

          {sessions.data!.map((s) => {
            const d = deviceOf(s.userAgent);
            const Icon = d.mobile ? Smartphone : Laptop;
            return (
              <div
                key={s.id}
                className={cn(
                  "grid grid-cols-[minmax(0,1fr)_200px_200px] items-center gap-4 border-b border-border px-5 py-3.5 last:border-b-0",
                  s.isCurrent && "bg-brand-50/40",
                )}
              >
                <span className="flex min-w-0 items-center gap-2.5 text-[14px]">
                  <Icon className="size-[18px] shrink-0 text-muted-foreground" />
                  <span className="truncate">{d.name}</span>
                  {s.isCurrent && (
                    <span className="shrink-0 rounded bg-brand-100 px-1.5 py-0.5 text-[11px] text-brand-700">
                      当前设备
                    </span>
                  )}
                </span>
                <span className="text-[13px] text-muted-foreground tabular-nums">
                  {new Date(s.createdAt).toLocaleString("zh-CN")}
                </span>
                <span className="text-[13px] text-muted-foreground tabular-nums">
                  {new Date(s.expiresAt).toLocaleString("zh-CN")}
                </span>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-5 flex items-center gap-4">
        {revokeOthers.isSuccess ? (
          <p className="text-[14px] text-emerald-700">已退出 {revokeOthers.data.revoked} 个设备</p>
        ) : (
          <>
            <Button
              variant="outline"
              loading={revokeOthers.isPending}
              disabled={others === 0}
              onClick={() => revokeOthers.mutate()}
            >
              {others === 0 ? "只有当前设备登录着" : `退出其他 ${others} 个设备`}
            </Button>
            {/* ⚠️ 说清楚不会把他自己踢下线，否则他不敢点。 */}
            <p className="text-[13px] text-muted-foreground">
              不会退出当前设备，你不用重新登录。
            </p>
          </>
        )}
      </div>
      {revokeOthers.error && <ErrorBlock error={revokeOthers.error} />}
    </AccountShell>
  );
}
